import { useLazyQuery, useQuery } from "@apollo/client";
import {
  GET_APPLICATION_STATUS,
  GET_APPLICATION_TAGS,
} from "graphql/queries/application";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SnapLoader } from "shared-components/snap-loader";
import Header from "shared-components/header/Header";
import awaitingDocuments from "assets/Incomplete_Application.svg";
import DocumentStatus from "./document-status";
import DragAndDropVgs from "shared-components/drag-and-drop-vgs";

type ApplicationStatusV2Props = { applicationId: string };

function ApplicationStatusV2({ applicationId }: ApplicationStatusV2Props) {
  const [orgKybId, setOrgKybId] = useState<string>();
  const [officerKycId, setOfficerKycId] = useState<string>();
  // todo: beneficial owners
  const [vaultId, setVaultId] = useState<string>();
  const [fileUploadStatuses, setFileUploadStatuses] = useState<
    { documentOwner?: string; documentName: string }[]
  >([]);
  const [documentName, setDocumentName] = useState<string>();
  const [unitDocumentId, setUnitDocumentId] = useState<string>();
  const [documentOwner, setDocumentOwner] = useState<string>();
  const { data, loading } = useQuery(GET_APPLICATION_STATUS, {
    variables: {
      provider: "unit",
      applicationId,
    },
    fetchPolicy: "network-only",
  });
  const [getApplicationTagsQuery, { loading: tagsAreLoading }] = useLazyQuery(
    GET_APPLICATION_TAGS,
    {
      variables: { unitApplicationId: applicationId },
    }
  );
  const navigate = useNavigate();
  useEffect(() => {
    if (data?.vaultAccountStatus.valid && !data?.vaultAccountStatus.pending) {
      navigate("/dashboard");
    }
  }, [
    data?.vaultAccountStatus.pending,
    data?.vaultAccountStatus.valid,
    navigate,
  ]);
  const hasRequiredDocuments =
    (
      data?.vaultAccountStatus.accountRepresentativeRequirements
        .requiredDocuments || []
    ).length ||
    (data?.vaultAccountStatus.kybRequirements.requiredDocuments || []).length ||
    (data?.vaultAccountStatus.beneficialOwnersRequirements || []).some(
      (r: { requiredDocuments: string[] }) => r.requiredDocuments.length
    );

  useEffect(() => {
    const requestTags = async () => {
      if (!hasRequiredDocuments) {
        return;
      }
      const tags = (await getApplicationTagsQuery())?.data
        ?.vaultUnitApplicationTagsFetch;
      setOrgKybId(tags?.kybId);
      setOfficerKycId(tags?.officerKycId);
    };
    requestTags();
  }, [getApplicationTagsQuery, hasRequiredDocuments]);

  const beneficialOwnersRequirementsTuples =
    data?.vaultAccountStatus.beneficialOwnersRequirements.reduce(
      (
        accumulator: [string, string][],
        beneficialOwnersRequirement: {
          requiredDocuments: string[];
          requiredDocumentIds: string[];
          requiredDocumentOwnerNames: string[];
        }
      ) => {
        return [
          ...accumulator,
          ...beneficialOwnersRequirement.requiredDocuments.map(
            (documentName, index) => [
              documentName,
              beneficialOwnersRequirement.requiredDocumentIds[index],
              beneficialOwnersRequirement.requiredDocumentOwnerNames[index],
            ]
          ),
        ];
      },
      []
    );

  return (
    <div className="App relative">
      <Header />
      <main className="pt-12 md:pt-14 left-0 right-0 lg:px-[35px] lg:pb-[35px] bg-gray-100 min-h-[90vh] bg-white">
        <div className="flex justify-center p-5 pt-10 md:pt-20">
          <div className="block">
            {loading && <SnapLoader />}
            {!loading && (
              <>
                {data?.vaultAccountStatus.valid &&
                  data?.vaultAccountStatus.pending && (
                    <p className="text-md">
                      Your application is still processing. Please check back
                      later.
                    </p>
                  )}
                {!data?.vaultAccountStatus.valid &&
                  data?.vaultAccountStatus.pending &&
                  hasRequiredDocuments &&
                  orgKybId &&
                  officerKycId && (
                    <div className="flex flex-col items-center lg:mx-72 mx-10">
                      <p className="text-lg font-semibold">
                        Awaiting Documents
                      </p>
                      <img
                        src={awaitingDocuments}
                        alt="for document under review"
                        className="mt-4"
                      />
                      <p className="text-sm text-center text-gray-500 my-4">
                        Looks like we need a little more information about you
                        to finish your request to access this program.
                      </p>
                      <DocumentStatus
                        kybRequirements={data.vaultAccountStatus.kybRequirements.requiredDocuments.map(
                          (document: string, index: number) => [
                            document,
                            data.vaultAccountStatus.kybRequirements
                              .requiredDocumentIds[index],
                            data.vaultAccountStatus.kybRequirements
                              .requiredDocumentOwnerNames[index],
                          ]
                        )}
                        accountRepresentativeRequirements={data.vaultAccountStatus.accountRepresentativeRequirements.requiredDocuments.map(
                          (document: string, index: number) => [
                            document,
                            data.vaultAccountStatus
                              .accountRepresentativeRequirements
                              .requiredDocumentIds[index],
                            data.vaultAccountStatus
                              .accountRepresentativeRequirements
                              .requiredDocumentOwnerNames[index],
                          ]
                        )}
                        beneficialOwnersRequirements={
                          beneficialOwnersRequirementsTuples
                        }
                        onDocumentChange={({
                          documentName,
                          unitId,
                          ownerName,
                        }) => {
                          setDocumentName(documentName);
                          setUnitDocumentId(unitId);
                          setDocumentOwner(ownerName);
                        }}
                        onVaultEntityTypeChange={(entityType) => {
                          switch (entityType) {
                            case "kyb":
                              setVaultId(orgKybId);
                              break;
                            case "representative":
                              setVaultId(officerKycId);
                              break;
                            case "beneficialOwners":
                              // todo set beneficial owners
                              break;
                          }
                        }}
                        fileUploadStatuses={fileUploadStatuses}
                      />
                      {documentName && vaultId && (
                        <DragAndDropVgs
                          documentName={documentName}
                          applicationId={applicationId}
                          vaultId={vaultId}
                          onUpload={() => {
                            setFileUploadStatuses([
                              ...fileUploadStatuses,
                              { documentName, documentOwner },
                            ]);
                            setDocumentName(undefined);
                          }}
                          unitDocumentId={unitDocumentId}
                        />
                      )}
                    </div>
                  )}
                {!data?.vaultAccountStatus.valid &&
                  (!data?.vaultAccountStatus.pending ||
                    !hasRequiredDocuments ||
                    (!tagsAreLoading && !orgKybId) ||
                    (!tagsAreLoading && !officerKycId)) && (
                    <p className="text-md text-red-500">
                      There is an issue with your application. Please contact
                      support.
                    </p>
                  )}
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

export default ApplicationStatusV2;
